import { type FullTrackingGraphQL, GrahpQLTrackingRequester, GraphQLCameraMediaRequester, GraphQLRoutingRequester, type NewRoute } from '@cobliteam/api-lib';
import { type DateRange, type EventFilters, EventTrigger, type Media, MediaType, type PageableContent } from '@cobliteam/common-types';
import { usePermissions } from '@cobliteam/permissions';
import Alert from '@cobliteam/ui/components/Alert';
import moment from 'moment';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
  loadMediaEventsFailed,
  loadMediaEventsRequest,
  loadMediaEventsSucceeded,
  loadRouteInfoFailed,
  loadSavedRoutesFailed,
  loadSavedRoutesRequest,
  loadSavedRoutesSucceeded,
  loadTrackingFailed,
  loadTrackingSucceeded,
} from './actionCreators';
import type * as Actions from './actions';
import { ActionTypes } from './actionTypes';

const trackingRequester = new GrahpQLTrackingRequester();
const routingGraphQLRequester = new GraphQLRoutingRequester();
const mediaGraphQLRequester = new GraphQLCameraMediaRequester();

export function* requestRouteInfoSaga(action: Actions.LoadRouteInfoRequest) {
  const { deviceId, vehicleId, date, mediaEventsEnabled } = action.payload;

  const dateMoment = moment(date);
  const start = dateMoment.startOf('day').valueOf();
  const end = dateMoment.endOf('day').valueOf();

  try {
    if (mediaEventsEnabled) {
      yield put(loadMediaEventsRequest(start, end, deviceId));
    }

    yield put(loadSavedRoutesRequest(date, vehicleId));
  } catch (err) {
    yield put(loadRouteInfoFailed());
  }
}

export function* requestTrackingGraphQLSaga(action: Actions.LoadTrackingRequest) {
  const { vehicleId, startTimestamp, endTimestamp, useMapMatch } = action.payload;

  try {
    const trackingGraphQL: FullTrackingGraphQL = yield call(trackingRequester.getFullTrackingInfo, vehicleId, startTimestamp, endTimestamp, useMapMatch);

    yield put(loadTrackingSucceeded(trackingGraphQL));
  } catch (err) {
    yield put(loadTrackingFailed());
  }
}

export function* loadSavedRoutesRequestSaga(action: Actions.LoadSavedRoutesRequest) {
  const { isAccessAllowed } = usePermissions();
  const { date, vehicleId } = action.payload;
  const period: DateRange = {
    start: moment(date).startOf('day').valueOf(),
    end: moment(date).endOf('day').valueOf(),
  };

  try {
    const newRoutes: NewRoute[] = yield call(routingGraphQLRequester.getTrackingRoutes, period, vehicleId, isAccessAllowed('view:proof-of-conclusion'));

    yield put(loadSavedRoutesSucceeded(newRoutes));
  } catch (err) {
    yield put(loadSavedRoutesFailed());
  }
}

export function* requestMediaEventsSaga(action: Actions.LoadMediaEventsRequest) {
  const { deviceId, startInMillis, endInMillis } = action.payload;

  try {
    const period: DateRange = {
      start: startInMillis,
      end: endInMillis,
    };

    const eventCount: number = yield call(mediaGraphQLRequester.getMediaEventCount, deviceId, period);

    const mediaFilters: EventFilters = {
      size: eventCount,
      mediaPeriod: period,
      vehicleIds: [deviceId],
      mediaTypes: [MediaType.EVENT_VIDEO],
      eventTriggers: [EventTrigger.TAILGATING, EventTrigger.DISTRACTED_DRIVING],
    };

    const events: PageableContent<Media> = yield call(mediaGraphQLRequester.getMediaEventList, mediaFilters, 0);

    yield put(loadMediaEventsSucceeded(events.content));
  } catch (err) {
    yield put(loadMediaEventsFailed());
    Alert.error('Houve um erro ao carregar os eventos de mídia.');
  }
}

export function* TrackingSagasListener() {
  yield all([
    takeLatest(ActionTypes.LOAD_TRACKING_REQUEST, requestTrackingGraphQLSaga),
    takeLatest(ActionTypes.LOAD_ROUTE_INFO_REQUEST, requestRouteInfoSaga),
    takeLatest(ActionTypes.LOAD_SAVED_ROUTES_REQUEST, loadSavedRoutesRequestSaga),
    takeLatest(ActionTypes.LOAD_MEDIA_EVENTS_REQUEST, requestMediaEventsSaga),
  ]);
}
